// Product translation utilities
// Extracts localized product titles from Shopify metafields

import type { ShopifyProduct } from './shopify';

export type Language = 'en' | 'lv' | 'ru';

// Extended metafield type that includes namespace
interface MetafieldWithNamespace {
  key: string;
  value: string;
  namespace?: string;
}

/**
 * Detect and fix corrupted translations where a phrase repeats endlessly.
 * Returns the cleaned value, or falls back to fallback if the value is garbage.
 */
function sanitizeTranslation(value: string, fallback: string): string {
  if (!value || value.length < 3) return value || fallback;
  
  // Detect non-Latin/non-Cyrillic script characters (Bengali, Devanagari, CJK, Arabic, etc.)
  const foreignScript = /[\u0900-\u097F\u0980-\u09FF\u0A00-\u0A7F\u0A80-\u0AFF\u0B00-\u0B7F\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F\u0E00-\u0E7F\u1000-\u109F\u3000-\u9FFF\uAC00-\uD7AF\u0600-\u06FF]/;
  if (foreignScript.test(value)) {
    return fallback;
  }
  
  // Detect any word/phrase repeating 3+ times consecutively
  // This catches "MAZGĀTUVES MAZGĀTUVES MAZGĀTUVES..." patterns
  const words = value.split(/\s+/);
  if (words.length > 6) {
    // Check for single-word repetition (same word 4+ times in a row)
    let repeatCount = 1;
    for (let i = 1; i < words.length; i++) {
      if (words[i].toLowerCase() === words[i - 1].toLowerCase()) {
        repeatCount++;
        if (repeatCount >= 4) return fallback;
      } else {
        repeatCount = 1;
      }
    }
    
    // Check for 2-word phrase repetition
    for (let phraseLen = 1; phraseLen <= 3; phraseLen++) {
      if (words.length < phraseLen * 4) continue;
      for (let start = 0; start <= words.length - phraseLen * 4; start++) {
        const phrase = words.slice(start, start + phraseLen).join(' ').toLowerCase();
        let consecutive = 1;
        let pos = start + phraseLen;
        while (pos + phraseLen <= words.length) {
          const next = words.slice(pos, pos + phraseLen).join(' ').toLowerCase();
          if (next === phrase) {
            consecutive++;
            pos += phraseLen;
            if (consecutive >= 3) return fallback;
          } else {
            break;
          }
        }
      }
    }
  }
  
  // Cap at reasonable length (max 300 chars for a title)
  if (value.length > 300) {
    return value.slice(0, 297) + '...';
  }
  
  return value;
}

/**
 * Get localized product title from metafields
 * Falls back to original title if translation not available
 */
export function getLocalizedTitle(
  product: ShopifyProduct['node'],
  language: Language
): string {
  const originalTitle = product.title;

  // Check metafields for translations (including EN override via title_en)
  const metafields = product.metafields as MetafieldWithNamespace[] | null | undefined;
  if (!metafields || !Array.isArray(metafields)) {
    return originalTitle;
  }

  const titleKey = `title_${language}`;

  // Check both 'custom' and 'global' namespaces for translations
  const translatedField = metafields.find(
    (m) => m?.key === titleKey && m?.value &&
           (m?.namespace === 'custom' || m?.namespace === 'global' || !m?.namespace)
  );

  return translatedField?.value
    ? sanitizeTranslation(translatedField.value, originalTitle)
    : originalTitle;
}

/**
 * Get localized title from a product node with direct access
 * Useful for product detail pages
 */
export function getProductTitle(
  metafields: Array<{ key: string; value: string; namespace?: string } | null> | null | undefined,
  originalTitle: string,
  language: Language
): string {
  if (!metafields || !Array.isArray(metafields)) {
    return originalTitle;
  }

  const titleKey = `title_${language}`;

  // Check both 'custom' and 'global' namespaces
  const translatedField = metafields.find(
    (m) => m?.key === titleKey && m?.value &&
           (m?.namespace === 'custom' || m?.namespace === 'global' || !m?.namespace)
  );

  return translatedField?.value
    ? sanitizeTranslation(translatedField.value, originalTitle)
    : originalTitle;
}

/**
 * Check if product has translations
 */
export function hasTranslations(
  product: ShopifyProduct['node']
): { lv: boolean; ru: boolean } {
  const metafields = product.metafields;

  if (!metafields || !Array.isArray(metafields)) {
    return { lv: false, ru: false };
  }

  return {
    lv: metafields.some(m => m?.key === 'title_lv' && m?.value),
    ru: metafields.some(m => m?.key === 'title_ru' && m?.value),
  };
}

/**
 * Get localized product description from metafields.
 * Falls back to the original product.description.
 */
export function getLocalizedDescription(
  product: ShopifyProduct["node"],
  language: Language
): string {
  const originalDescription = product.description || "";

  const metafields = product.metafields as MetafieldWithNamespace[] | null | undefined;
  if (!metafields || !Array.isArray(metafields)) {
    return originalDescription;
  }

  const descriptionKey = `description_${language}`;
  const translatedField = metafields.find(
    (m) =>
      m?.key === descriptionKey &&
      m?.value &&
      (m?.namespace === "custom" || m?.namespace === "global" || !m?.namespace)
  );

  return translatedField?.value
    ? sanitizeTranslation(translatedField.value, originalDescription)
    : originalDescription;
}
